import React, { useCallback, useEffect, useRef, useState } from "react";
import { legacyPortalAPI } from "@depmap/api";
import { toStaticUrl } from "@depmap/globals";
import {
  ContextExplorerDatasets,
  EnrichedLineagesTileData,
} from "@depmap/types";
import InfoIcon from "src/common/components/InfoIcon";
import PlotSpinner from "src/plot/components/PlotSpinner";
import ExtendedPlotType from "src/plot/models/ExtendedPlotType";
import CollapsibleBoxPlots from "./boxPlots/CollapsibleBoxPlots";

interface EnrichmentTileProps {
  featureLabel: string;
  featureType: string;
}

export const EnrichmentTile: React.FC<EnrichmentTileProps> = ({
  featureLabel,
  featureType,
}) => {
  const contextExplorerHref = `${
    window.location.href.split(encodeURIComponent(featureType))[0]
  }context_explorer`;

  const [tileData, setTileData] = useState<EnrichedLineagesTileData | null>(
    null
  );
  const [isLoadingBoxplot, setIsLoadingBoxplot] = useState<boolean>(true);
  // const [boxplotError, setBoxplotError] = useState(false);
  const boxplotLatestPromise = useRef<Promise<EnrichedLineagesTileData> | null>(
    null
  );
  useEffect(() => {
    setTileData(null);
    // setEntityDetailMainPlotElement(null);
    setIsLoadingBoxplot(true);
    // setBoxplotError(false);
    const boxplotPromise = legacyPortalAPI.getEnrichmentTileData(
      "Lineage",
      featureType,
      featureLabel
    );

    boxplotLatestPromise.current = boxplotPromise;

    boxplotPromise
      .then((dataVals) => {
        if (boxplotPromise === boxplotLatestPromise.current) {
          setTileData(dataVals);
        }
      })
      .catch((e) => {
        if (boxplotPromise === boxplotLatestPromise.current) {
          window.console.error(e);
          // setBoxplotError(true);
        }
      })
      .finally(() => setIsLoadingBoxplot(false));
  }, [setIsLoadingBoxplot, featureType, featureLabel]);

  const getTabFromDatasetName = useCallback((datasetName: string) => {
    if (datasetName === ContextExplorerDatasets.Chronos_Combined.toString()) {
      return "geneDependency";
    }

    if (
      datasetName ===
      ContextExplorerDatasets.Rep_all_single_pt_per_compound.toString()
    ) {
      return "repurposing";
    }

    if (
      datasetName ===
      ContextExplorerDatasets.PRISMOncologyReferenceLog2AUCMatrix.toString()
    ) {
      return "oncrefLum";
    }

    if (
      datasetName ===
      ContextExplorerDatasets.PRISMOncologyReferenceUnifiedLog2AUCMatrix.toString()
    ) {
      return "oncrefUnified";
    }

    return "oncrefSeq";
  }, []);

  if (
    !isLoadingBoxplot &&
    (!tileData?.box_plot_data.significant_selection ||
      tileData?.box_plot_data.significant_selection.length === 0)
  ) {
    if (
      tileData?.box_plot_data.insignificant_heme_data?.data.length === 0 ||
      tileData?.box_plot_data.insignificant_solid_data?.data.length === 0
    ) {
      return null;
    }
  }

  const customInfoImg = (
    <img
      style={{
        height: "13px",
        margin: "1px 3px 4px 3px",
        cursor: "pointer",
      }}
      src={toStaticUrl("img/gene_overview/info_purple.svg")}
      alt="description of term"
      className="icon"
    />
  );

  const getCompoundToolTip = () => {
    if (
      tileData?.dataset_name ===
        ContextExplorerDatasets.PRISMOncologyReferenceLog2AUCMatrix ||
      tileData?.dataset_name ===
        ContextExplorerDatasets.PRISMOncologyReferenceSeqLog2AUCMatrix ||
      tileData?.dataset_name ===
        ContextExplorerDatasets.PRISMOncologyReferenceUnifiedLog2AUCMatrix
    ) {
      return `Lineages and/or subtypes that have, on average, a stronger sensitivity to this compound compared to all other models. Enriched lineages/subtypes are calculated as in Context Explorer and selected based on default Context Explorer filters (T-test FDR<0.1, avg. AUC difference < -0.1).`;
    }
    return "Lineages and/or subtypes that have, on average, a stronger sensitivity to this compound compared to all other models. Enriched lineages/subtypes are calculated as in Context Explorer and selected based on default Context Explorer filters (T-test FDR<0.1, avg. log2(Viability) difference < -0.5).";
  };

  const tooltipText =
    featureType === "gene"
      ? "Lineages and/or subtypes that have, on average, a stronger dependency on this gene compared to all other models. Enriched lineages/subtypes are calculated as in Context Explorer and selected based on default Context Explorer filters (T-test FDR<0.1, avg. gene effect difference < -0.25 and min. 1 dependent in-group model)."
      : getCompoundToolTip();

  return (
    <article className="card_wrapper stacked-boxplot-tile">
      <div className="card_border container_fluid">
        <h2 className="no_margin cardtitle_text">
          Enriched Lineages{" "}
          {tileData && (
            <InfoIcon
              target={customInfoImg}
              popoverContent={<p>{tooltipText}</p>}
              popoverId={`sidebar-genedep1-popover`}
              trigger={["hover", "focus"]}
            />
          )}
        </h2>
        <div className="card_padding">
          {featureType === "gene" ? (
            <h4 className="crispr">{tileData?.dataset_display_name}</h4>
          ) : (
            <h4>{tileData?.dataset_display_name}</h4>
          )}
        </div>
        <div className="card_padding stacked-boxplot-graphs-padding">
          <div id="enrichment-tile">
            {!tileData && isLoadingBoxplot && <PlotSpinner />}
            {tileData && (
              <CollapsibleBoxPlots
                handleSetMainPlotElement={(
                  element: ExtendedPlotType | null
                ) => {
                  if (element) {
                    /* do nothing */
                  }
                }}
                topContextNameInfo={tileData.top_context_name_info}
                selectedCode={tileData.selected_context_name_info?.subtype_code}
                boxPlotData={tileData.box_plot_data}
                featureType={featureType}
                urlPrefix={contextExplorerHref}
                tab={getTabFromDatasetName(tileData.dataset_name)}
                datasetId={
                  ContextExplorerDatasets[
                    tileData.dataset_name as ContextExplorerDatasets
                  ]
                }
              />
            )}
          </div>
          {tileData && (
            <p className="stacked-boxplot-download-container">
              Click a context name above to view this enrichment and additional
              subtype-specific results in Context Explorer
            </p>
          )}
        </div>
      </div>
    </article>
  );
};
